import { type CSSProperties } from "react";
import type { WizardController } from "../../../hooks/use-wrapped-api-wizard";
import { wizardStyles, ConfidenceBadge, MethodBadge } from "./wizard-ui";

/**
 * Step 3 — Operations. The AI recommends a state per operation (GET → enabled,
 * writes → review, DELETE → disabled). GET tools are always live; write ops are
 * opt-in toggles the owner adjusts. "Continue" persists the enabled set.
 */
export function Step3Operations({ wizard }: { wizard: WizardController }) {
  const ops = wizard.proposal?.operations ?? [];
  const reads = ops.filter((o) => o.method === "GET");
  const writes = ops.filter((o) => o.method !== "GET");

  return (
    <div>
      <p style={wizardStyles.intro}>
        Read operations go live immediately. Write operations are off by default — enable only the ones you need.
        Destructive operations (DELETE) start disabled with a warning.
      </p>

      {reads.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={wizardStyles.sectionTitle}>Read operations · {reads.length} (always live)</div>
          {reads.map((o) => (
            <div key={o.operation_id} style={row}>
              <MethodBadge method={o.method} />
              <div style={opMeta}>
                <div style={opName} title={o.path}>{o.operation_id}</div>
                <div style={opSummary}>{o.summary}</div>
              </div>
              <span style={lockedBadge}>
                <i className="ti ti-lock" style={{ fontSize: 10 }} aria-hidden="true" /> live
              </span>
            </div>
          ))}
        </div>
      )}

      {writes.length > 0 && (
        <div>
          <div style={wizardStyles.sectionTitle}>Write operations · {writes.length} (opt-in)</div>
          {writes.map((o) => {
            const on = wizard.enabledOps.has(o.operation_id);
            const destructive = o.method === "DELETE";
            return (
              <div key={o.operation_id} style={row}>
                <MethodBadge method={o.method} />
                <div style={opMeta}>
                  <div style={opName} title={o.path}>
                    {o.operation_id}
                    {destructive && (
                      <span style={{ color: "var(--c-red-600, var(--color-text-secondary))", marginLeft: 6, fontSize: 11 }}>
                        <i className="ti ti-alert-triangle" style={{ fontSize: 11 }} aria-hidden="true" /> destructive
                      </span>
                    )}
                  </div>
                  <div style={opSummary}>{o.reason}</div>
                </div>
                <ConfidenceBadge confidence={o.confidence} />
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  onClick={() => wizard.toggleOp(o.operation_id, !on)}
                  style={toggle(on)}
                  title={on ? "Enabled — click to disable" : "Disabled — click to enable"}
                >
                  <span style={toggleKnob(on)} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {ops.length === 0 && <div style={wizardStyles.hint}>No operations were found in the spec.</div>}
    </div>
  );
}

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 0",
  borderBottom: "0.5px solid var(--color-border-secondary)",
};
const opMeta: CSSProperties = { flex: 1, minWidth: 0 };
const opName: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-mono)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const opSummary: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const lockedBadge: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  fontSize: 10,
  color: "var(--c-green-800, var(--color-text-secondary))",
  background: "var(--c-green-50, var(--color-background-secondary))",
  borderRadius: "var(--border-radius-sm)",
  padding: "2px 7px",
  flexShrink: 0,
};

function toggle(on: boolean): CSSProperties {
  return {
    width: 36,
    height: 20,
    flexShrink: 0,
    borderRadius: 10,
    border: "none",
    cursor: "pointer",
    background: on ? "var(--color-text-info)" : "var(--color-background-tertiary, var(--color-border-secondary))",
    position: "relative",
    transition: "background 0.12s",
    padding: 0,
  };
}
function toggleKnob(on: boolean): CSSProperties {
  return {
    position: "absolute",
    top: 2,
    left: on ? 18 : 2,
    width: 16,
    height: 16,
    borderRadius: "50%",
    background: "var(--color-background-primary)",
    transition: "left 0.12s",
  };
}
